import { forwardRef } from 'react';

import { Modal as MuiModal } from '@mui/material';
import { IconX } from '@tabler/icons-react';

import useModalZIndex from '@hooks/useModalZIndex';
import { Text, Button, Breadcrumb } from '@fragments';

import './styles.scss';

// Named widths for the centered surface. `max-width: 96vw` (in styles.scss)
// keeps any of these from overflowing on narrow viewports.
const SIZE_WIDTHS = {
    sm: 420,
    md: 520,
    lg: 680,
};

/**
 * Modal — a centered dialog built on MUI `Modal` (portal + backdrop + focus
 * trap), styled with our surface tokens. Declarative: open/close are owned by
 * the caller.
 *
 * @param {boolean} open                 controls visibility
 * @param {() => void} onClose           fired on backdrop click, ESC, and the close button
 * @param {string} [title]               primary heading (Text.h4)
 * @param {string} [subtitle]            secondary line under the title (muted)
 * @param {string} [eyebrow]             tiny UPPERCASE label above the title (Text.subheading)
 * @param {Array|React.ReactNode} [breadcrumb] step/path trail rendered at the very top of the
 *                                       header, above the eyebrow/title. An array is passed
 *                                       straight to the Breadcrumb fragment as `items`; any
 *                                       other node is rendered as-is
 * @param {'sm'|'md'|'lg'|number} [size] surface width — preset name or raw px. Default 'md'
 * @param {React.ReactNode} [footer]     footer node (typically right-aligned action Buttons)
 * @param {boolean} [hideClose]          hide the top-right close button. Default false
 * @param {'default'|'card'|'flat'} [variant]
 *                                       'card' drops the header and the body padding so
 *                                       media can touch the surface's top corners, floats
 *                                       the close button over that media, and stacks the
 *                                       footer actions vertically at full width.
 *                                       'flat' drops only the header: the padded body owns
 *                                       the whole surface (a centered hero + form, say), the
 *                                       close button floats in the top-right corner and the
 *                                       footer loses its divider. Default 'default'
 * @param {string} [closeLabel]          aria-label / tooltip for the floating close button
 *                                       (card and flat variants)
 * @param {React.ReactNode} [backdrop]   ambient layer painted behind header, body AND footer,
 *                                       covering the whole surface (e.g. a blurred image)
 * @param {string} [className]           extra class on the surface
 * @param {React.ReactNode} children     body content
 */
const Modal = forwardRef(function Modal(
    {
        open,
        onClose,
        title,
        subtitle,
        eyebrow,
        breadcrumb,
        size = 'md',
        footer,
        hideClose = false,
        variant = 'default',
        closeLabel,
        backdrop,
        className = '',
        children,
        ...props
    },
    ref,
) {
    const { zIndex, isTop } = useModalZIndex(open);

    // Resolve the surface width: preset key → px from SIZE_WIDTHS, raw number →
    // px as-is, anything else → md fallback.
    const width =
        typeof size === 'number'
            ? size
            : SIZE_WIDTHS[size] ?? SIZE_WIDTHS.md;

    const isCard = variant === 'card';
    const isFlat = variant === 'flat';

    // Neither the card nor the flat variant draws the header block: card media
    // owns the top of the surface and a flat body owns all of it — the close
    // button floats over both instead.
    const hasHeader = !isCard && !isFlat && !!(breadcrumb || eyebrow || title || subtitle || !hideClose);

    return (
        <MuiModal
            open={open}
            onClose={onClose}
            sx={{ '--modal-z': zIndex }}
            disableEnforceFocus={!isTop}
        >
            <div
                ref={ref}
                className={`fragment-modal ${isCard ? 'fragment-modal--card' : ''} ${isFlat ? 'fragment-modal--flat' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
                style={{ '--modal-width': `${width}px` }}
                {...props}
            >
                {backdrop && (
                    <div className="fragment-modal__backdrop" aria-hidden="true">
                        {backdrop}
                    </div>
                )}

                {isCard && !hideClose && (
                    <Button
                        variant="rounded"
                        color="current"
                        size="small"
                        icon={<IconX />}
                        onClick={onClose}
                        tooltip={closeLabel}
                        aria-label={closeLabel}
                        className="fragment-modal__close fragment-modal__close--floating"
                    />
                )}

                {isFlat && !hideClose && (
                    <Button
                        variant="text"
                        color="inverse"
                        size="small"
                        icon={<IconX />}
                        onClick={onClose}
                        tooltip={closeLabel}
                        aria-label={closeLabel}
                        className="fragment-modal__close fragment-modal__close--floating"
                    />
                )}

                {hasHeader && (
                    <div className="fragment-modal__header">
                        <div className="fragment-modal__heading">
                            {breadcrumb && (
                                <div className="fragment-modal__breadcrumb">
                                    {Array.isArray(breadcrumb)
                                        ? <Breadcrumb items={breadcrumb} />
                                        : breadcrumb}
                                </div>
                            )}
                            {eyebrow && (
                                <Text.subheading className="fragment-modal__eyebrow">
                                    {eyebrow}
                                </Text.subheading>
                            )}
                            {title && (
                                <Text.h4 weight={500} contrast={9}>
                                    {title}
                                </Text.h4>
                            )}
                            {subtitle && (
                                <Text.span contrast={6} className="fragment-modal__subtitle">
                                    {subtitle}
                                </Text.span>
                            )}
                        </div>

                        {!hideClose && (
                            <Button
                                variant="text"
                                color="inverse"
                                size="small"
                                icon={<IconX />}
                                onClick={onClose}
                                className="fragment-modal__close"
                                aria-label={closeLabel}
                            />
                        )}
                    </div>
                )}

                <div className="fragment-modal__body">{children}</div>

                {footer && <div className="fragment-modal__footer">{footer}</div>}
            </div>
        </MuiModal>
    );
});

export default Modal;
